import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
import { getGhToken } from "./gh-helpers.js";
import { LocalCliError } from "./errors.js";
import { extractTarGz, publishAtomically } from "./tarball-cache.js";
import { hydrateAgentsInto, type AgentContentDeps } from "./agent-content.js";
import type { ReleaseManifest } from "./release-manifest.js";

const OWNER_REPO = "m8t-labs/m8t";

export interface RepoTree { treeShaOf(p: string): string | undefined }
interface RawTreeEntry { path: string; type: string; sha: string }

export function parseTreeResponse(body: { tree?: RawTreeEntry[] }): RepoTree {
  const map = new Map<string, string>();
  for (const e of body.tree ?? []) if (e.type === "tree") map.set(e.path, e.sha);
  return { treeShaOf: (p) => map.get(p.replace(/\/$/, "")) };
}

export async function fetchRepoTree(
  commit: string,
  deps: { fetchImpl?: typeof fetch; ghToken?: () => Promise<string> } = {},
): Promise<RepoTree> {
  const fetchImpl = deps.fetchImpl ?? fetch;
  const ghToken = deps.ghToken ?? getGhToken;
  let token: string | undefined;
  try { token = await ghToken(); } catch { token = undefined; }
  const url = `https://api.github.com/repos/${OWNER_REPO}/git/trees/${commit}?recursive=1`;
  const res = await fetchImpl(url, {
    headers: { Accept: "application/vnd.github+json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) {
    throw new LocalCliError({ code: "PLATFORM_TREE_FETCH_FAILED", message: `GET ${url} → HTTP ${res.status.toString()}.` });
  }
  return parseTreeResponse((await res.json()) as { tree?: RawTreeEntry[] });
}

/**
 * Check each persona's manifest pin against the platform repo's tree at the
 * manifest's commit. Returns human-readable problems ([] === healthy).
 *
 * Two provenances, and the distinction is load-bearing:
 *
 * - **Agent-sourced** (`item.source` present) — the bytes live in a standalone
 *   agent repo and their `treeSha` is a content digest already verified BY VALUE
 *   at hydration. The platform tree has nothing to say about them, so they are
 *   skipped EXPLICITLY.
 * - **Platform-sourced** — the persona is in this repo at this commit, so a tree
 *   entry MUST exist. It previously did not: a missing entry fell through the
 *   `actual !== undefined` guard and passed silently. Once agent content moved
 *   out, every extracted persona would have verified vacuously through that same
 *   hole, and a genuinely corrupt manifest would too. A missing entry is now a
 *   reported problem.
 */
export function verifyPersonaTrees(manifest: ReleaseManifest, tree: RepoTree): string[] {
  const out: string[] = [];
  for (const [name, item] of Object.entries(manifest.components.personas.items)) {
    if (item.source) continue; // verified by content digest, not by this tree
    const actual = tree.treeShaOf(item.path);
    if (actual === undefined) {
      out.push(
        `persona '${name}' (${item.path}): the manifest claims it is in the platform repo at ${manifest.platform.commit}, but that commit's tree has no such path`,
      );
    } else if (actual !== item.treeSha) {
      out.push(`persona '${name}' (${item.path}): manifest pins ${item.treeSha} but commit tree has ${actual}`);
    }
  }
  return out;
}

/**
 * Resolve a repo-root-shaped directory of release content at manifest.platform.commit.
 * Canonical: one authed tarball fetch → atomic-rename into a per-commit cache.
 * `--content-dir` short-circuits to an operator-supplied checkout (trusted).
 */
export async function resolveReleaseContent(
  manifest: ReleaseManifest,
  opts: { contentDir?: string; cacheRoot?: string; fetchImpl?: typeof fetch; ghToken?: () => Promise<string> } = {},
): Promise<string> {
  if (opts.contentDir) {
    if (!fs.existsSync(path.join(opts.contentDir, "personas"))) {
      throw new LocalCliError({ code: "PLATFORM_CONTENT_DIR_INVALID", message: `--content-dir '${opts.contentDir}' has no personas/ — not a repo checkout.` });
    }
    return opts.contentDir;
  }
  const commit = manifest.platform.commit;
  const cacheRoot = opts.cacheRoot ?? path.join(os.homedir(), ".m8t", "cache");
  const dest = path.join(cacheRoot, `release-${commit}`);
  if (fs.existsSync(path.join(dest, "personas"))) return dest; // cache hit

  const fetchImpl = opts.fetchImpl ?? fetch;
  const ghToken = opts.ghToken ?? getGhToken;
  let token: string | undefined;
  try { token = await ghToken(); } catch { token = undefined; }
  const url = `https://api.github.com/repos/${OWNER_REPO}/tarball/${commit}`;
  const res = await fetchImpl(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) {
    throw new LocalCliError({ code: "PLATFORM_TARBALL_FETCH_FAILED", message: `GET ${url} → HTTP ${res.status.toString()}.` });
  }
  fs.mkdirSync(cacheRoot, { recursive: true });
  const tmp = fs.mkdtempSync(path.join(cacheRoot, `.dl-${commit.slice(0, 8)}-`));
  try {
    const tgz = path.join(tmp, "src.tar.gz");
    fs.writeFileSync(tgz, Buffer.from(await res.arrayBuffer()));
    // Extract, stripping the "m8t-labs-m8t-<sha>/" top-level prefix (tar --strip-components=1).
    const unpacked = path.join(tmp, "unpacked");
    fs.mkdirSync(unpacked);
    extractTarGz(tgz, unpacked); // strips 1 leading component
    // Atomic publish: rename into place (no poisoned partial cache on crash).
    publishAtomically({
      from: unpacked,
      dest,
      sentinel: "personas",
      errorCode: "PLATFORM_CACHE_PUBLISH_FAILED",
    });
  } finally {
    fs.rmSync(tmp, { recursive: true, force: true });
  }
  return dest;
}

/**
 * `resolveReleaseContent`, plus the agent content this release consumed.
 *
 * THE entry point for the update rail. Resolving release content and hydrating
 * agent content are one operation, not two, because a content root missing an
 * agent's persona is not a usable content root — and splitting them is how a
 * fourth call site eventually forgets the second half.
 *
 * The overlay is written into the per-commit cache after that cache was atomically
 * published. That is safe: agent bytes are digest-verified before they are written,
 * so a concurrent writer racing here writes identical content — and the hydration
 * is idempotent (replace, not merge) rather than additive.
 *
 * Manifests cut before agent repos existed carry no `agents`, which resolves to a
 * no-op. Correct: those releases genuinely had no agent-sourced content.
 */
export async function resolveReleaseContentWithAgents(
  manifest: ReleaseManifest,
  opts: {
    contentDir?: string;
    cacheRoot?: string;
    fetchImpl?: typeof fetch;
    ghToken?: () => Promise<string>;
    agentDeps?: AgentContentDeps;
  } = {},
): Promise<string> {
  const contentDir = await resolveReleaseContent(manifest, opts);
  await hydrateAgentsInto(contentDir, manifest.components.agents ?? {}, opts.agentDeps);
  return contentDir;
}
